import { ApiProperty, ApiPropertyOptional, OmitType } from '@nestjs/swagger';
import { Exclude, Expose, Type } from 'class-transformer';
import { BumpResourceSheetInterface } from '../interfaces/bump-resource-sheet.interface';
import { JobPhaseBumpCommonDto } from '../../job-phase-bump/dtos/job-phase-bump-common.dto';
import { BumpResourceSheetType } from '../enums/bump-resource-sheet.enum';
import { RepairTypeEnum } from '../../repair/enums/repair-type.enum';
import { PhaseTypeEnum } from '../../phase/enums/phase-type.enum';
import { RepairPaymentTypeEnum } from '../../repair-formula/enums/repair-payment-type.enum';
import {
  IsNumber,
  IsOptional,
  IsString,
  Min,
  ValidateNested,
} from 'class-validator';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';

@Exclude()
export class BumpResourceSheetDto
  extends OmitType(JobPhaseBumpCommonDto, ['currentCost', 'check', 'paidDate'])
  implements BumpResourceSheetInterface
{
  @ApiProperty({
    title: 'countPaidHistory',
    type: 'number',
  })
  @Expose()
  countPaidHistory!: number | null;

  @ApiProperty({
    title: 'Block',
    description: 'The block number/identifier',
    minLength: 1,
    maxLength: 100,
    nullable: true,
  })
  @IsOptional()
  @IsString()
  @Expose()
  jobBlock!: string | null;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Subdivision ID',
  })
  @Expose()
  subdivisionId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Builder ID',
  })
  @Expose()
  builderId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Job Repair ID',
  })
  @Expose()
  jobRepairId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Repair ID',
  })
  @Expose()
  repairId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Supervisor User ID',
  })
  @Expose()
  supervisorUserIds!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Supervisor Ful lNames',
  })
  @Expose()
  supervisorFullNames!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Created ByUser ID',
  })
  @Expose()
  createdById!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Created By (User) Name',
  })
  @Expose()
  createdByName!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Region ID',
  })
  @Expose()
  regionId!: string;

  @ApiProperty({
    type: 'string',
    format: 'uuid',
    title: 'Associated Contractor ID',
  })
  @Expose()
  contractorId!: string;

  @ApiProperty({
    title: 'Contractor name',
    type: 'string',
  })
  @Expose()
  contractorName!: string;

  @ApiProperty({
    title: 'Subdivision name',
    type: 'string',
  })
  @Expose()
  subdivisionName!: string;

  @ApiProperty({
    title: 'Builder name',
    type: 'string',
  })
  @Expose()
  builderName!: string;

  @ApiProperty({
    title: 'Total Payable Boards',
    type: 'number',
  })
  @Expose()
  payableBoardsTotal!: number | null;

  @ApiProperty({
    title: 'Lot',
    description: 'The lot number/identifier',
  })
  @Expose()
  jobLot!: string;

  @ApiProperty({
    title: 'Total worked hours',
    type: 'number',
  })
  @Expose()
  workedHours!: number;

  @ApiProperty({
    title: 'Total worked hours',
    type: 'number',
  })
  @Expose()
  calculateCost!: number;

  @ApiProperty({
    type: 'enum',
    enum: RepairPaymentTypeEnum,
    nullable: true,
    title: 'Repair Payment Type',
  })
  @IsOptional()
  @Expose()
  repairPaymentType!: RepairPaymentTypeEnum | null;

  @ApiProperty({
    title: 'Is Logged hours adjusted?',
    type: 'boolean',
  })
  @Expose()
  isLoggedHoursAdjusted!: boolean;

  @ApiProperty({
    title: 'Date',
    type: 'string',
    format: 'date-time',
  })
  @Expose()
  repairDate!: Date;

  @ApiProperty({
    type: 'string',
    title: 'Repair Type',
  })
  @Expose()
  repairType!: RepairTypeEnum | PhaseTypeEnum;

  @ApiProperty({
    type: 'enum',
    enum: BumpResourceSheetType,
  })
  @Expose()
  bumpResourceType!: BumpResourceSheetType;

  @ApiProperty({
    title: 'Window start time',
    type: 'string',
  })
  @Expose()
  windowStartTime!: string;

  @ApiProperty({
    title: 'Window end time',
    type: 'string',
  })
  @Expose()
  windowEndTime!: string;

  @ApiProperty({
    title: 'Address 1',
    type: 'string',
  })
  @Expose()
  address1!: string;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Job Geo',
    description: 'GPS location of the job associated with this bump record.',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  jobGeo!: GeographyPointDto | null;

  @ApiPropertyOptional({
    title: 'Job Geo Tolerance',
    description:
      'Distance tolerance in meters used for geo matching on this job.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  jobGeoTolerance!: number | null;

  @ApiProperty({
    title: 'Full Address',
    type: 'string',
  })
  @Expose()
  fullAddress!: string;

  @ApiProperty({
    title: 'Has Photos',
    type: 'string',
  })
  @Expose()
  hasPhotos!: boolean;

  @ApiProperty({
    title: 'Has Notes',
    type: 'string',
  })
  @Expose()
  hasNotes!: boolean;

  @ApiProperty({
    title: 'Logged By User Name',
    type: 'string',
    nullable: true,
  })
  @IsOptional()
  @IsString()
  @Expose()
  loggedByUserName!: string | null;

  @ApiProperty({
    title: 'Approved By User Name',
    type: 'string',
    nullable: true,
  })
  @IsOptional()
  @IsString()
  @Expose()
  approvedByUserName!: string | null;
}
